"use client";

import { Checkbox, Input, Select } from "@/components";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { verifyOffer as verifyOfferMutation } from "@/queries/verify-course-offer";
import BundleOffers from "./bundle-offers";
import { BundleOffer } from "@/queries/get-cohort-details.types";
import {
  Country,
  getCountryCallingCode,
  isValidPhoneNumber,
} from "react-phone-number-input";
import dynamic from "next/dynamic";
import { dataContext } from "@/contexts/cohort-data";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useSearchParams } from "next/navigation";
import { getCohortDetails } from "@/queries/get-cohort-details";
import {
  DEFAULT_LOCALIZATION_CODE,
  RIGI_OFFERING_TYPE,
} from "@/utils/constants";
import { DatePicker } from "@/components/date-picker";
import MobilePaymentFooter from "@/components/mobile-payment-footer";
import { DiscountDetails, View } from "./main";
import { useAnalytics } from "@/hooks/use-analytics";
import {
  calculateAmount,
  getCurrentTimestamp,
  throttle,
} from "@/utils/helpers";
import useDevice from "@/hooks/use-device";
import { MoneyCommonProps, SetIsPaymentLoadingType } from "@/types/payment";
import { SelectPaymentMethodModalData } from "@/types/payment-method";
import { getAmountBreakups } from "@/queries/get-amount-breakups";
import SelectPaymentMethod from "@/components/selectPaymentMethod";

const PhoneInput = dynamic(() => import("../../../../components/phone-input"), {
  ssr: false,
  loading: () => (
    <Input
      className="pl-[50px] text-primary"
      name="phone"
      label="Phone (WhatsApp enabled to access later)"
      placeholder="Enter Phone"
    />
  ),
});

const defaultSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters long"),
  email: z.string().email("Invalid email address"),
  phone: z.string().refine(isValidPhoneNumber, "Invalid phone number"),
  needGst: z.boolean().default(false),
  gstin: z.string().optional(),
  companyName: z.string().optional(),
  couponCode: z.string().optional(),
  offers: z.array(z.any()).optional(),
  tnc: z.coerce.boolean().refine((bool) => bool, {
    message: "You must agree to terms and conditions",
  }),
  kycDetails: z
    .object({
      type: z.string(),
      label: z.string(),
      key: z.string(),
      required: z.boolean().optional(),
      errorMessage: z.string().nullable().optional(),
      placeholder: z.string().nullable().optional(),
      regex: z.string().nullable().optional(),
      option: z.string().array().nullable().optional(),
      answer: z.string().optional(),
    })
    .refine(
      ({ regex, answer, required, label }) => {
        if (regex) {
          if (!answer) return false;
          return new RegExp(regex).test(answer);
        }
        const valid = required
          ? answer !== null && answer !== undefined && answer.trim().length > 0
          : true;
        return valid;
      },
      {
        message: `This field appears to be invalid.`,
        path: ["answer"],
      }
    )
    .array()
    .optional(),
});

const noGstSchema = z.object({
  needGst: z.literal(false),
});

const gstSchema = z.object({
  needGst: z.literal(true),
  gstin: z
    .string()
    .regex(
      /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/g,
      "Invalid GSTIN"
    ),
  companyName: z
    .string()
    .min(3, "Company name must be at least 3 characters long"),
});

const _schema = z.discriminatedUnion("needGst", [gstSchema, noGstSchema]);

const schema = z.intersection(_schema, defaultSchema);

export type Schema = z.infer<typeof schema>;

const Payment = ({
  slug,
  isPaymentLoading,
  initiatePayment,
  setDiscountDetails,
  view,
  setIsPaymentLoading,
}: {
  slug: string;
  isPaymentLoading: boolean;
  initiatePayment: (totalAmount: number, data: Schema) => void;
  setDiscountDetails: React.Dispatch<React.SetStateAction<DiscountDetails>>;
  setIsPaymentLoading: SetIsPaymentLoadingType;
  view: View;
}) => {
  const { data, setData } = useContext(dataContext);
  const code = useRef(DEFAULT_LOCALIZATION_CODE);
  const enabled = useRef(false);

  const hideCouponCodeField = data?.meta_data?.hide_coupon_code ?? false;

  const newTnC = data.kycDetails?.filter(
    (detail) => detail?.type === "tnc"
  )?.[0];
  const tncDefaultValue = newTnC?.default_checked !== false;
  const { track } = useAnalytics({ ...(data?.gtm_source ?? {}) });
  const { browser, os, url } = useDevice();

  const eventData = {
    url: url,
    creatorID: data?.creatorID,
    offeringStatus: data?.c_status,
    browser: browser,
    os: os,
    timestamp: getCurrentTimestamp(),
    product_type: "course",
  };

  const { control, watch, handleSubmit, getValues, setValue, ...rest } =
    useForm<Schema>({
      defaultValues: {
        email: "",
        name: "",
        needGst: false,
        phone: "",
        companyName: "",
        gstin: "",
        tnc: tncDefaultValue,
        couponCode: "",
        offers:
          data?.meta_data?.bundle_offers?.offers?.map((offer) => ({
            ...offer,
            isApplied: false,
          })) ?? [],
        kycDetails: data.kycDetails
          ? data.kycDetails
              .filter(({ type }) => type !== "tnc")
              .map((detail) =>
                detail.type === "dropdown"
                  ? {
                      ...detail,
                      answer: detail.option?.[0] || "",
                    }
                  : {
                      ...detail,
                      answer: "",
                    }
              )
          : undefined,
      },
      resolver: zodResolver(schema),
    });

  // Set initial offers
  useEffect(() => {
    setValue(
      "offers",
      data?.meta_data?.bundle_offers?.offers?.map((offer) => ({
        ...offer,
        isApplied: false,
      })) ?? []
    );
  }, [setValue, data?.meta_data?.bundle_offers?.offers]);

  const [discount, setDiscount] = useState<number | null>(null);
  const [discountPercentage, _setDiscountPercentage] = useState<number | null>(
    null
  );

  const setDiscountPercentage = useCallback((percentage: null | number) => {
    _setDiscountPercentage(percentage);
  }, []);

  const onCountryChange = useCallback(
    async (country: Country) => {
      if (!country || data.is_international_payment_enabled === false) return;
      const _code = getCountryCallingCode(country);
      enabled.current = true;
      code.current = _code;

      const _data = await getCohortDetails(slug, "+" + _code);

      setData((data) => ({
        ...data,
        ..._data,
        __country: country,
      }));
    },
    [data.is_international_payment_enabled, setData, slug]
  );
  const trackPayButtonClickEvent = () => {
    track("purchase_cta_click", eventData);
  };
  const trackApplyCouponEvent = (view: string) => {
    track("coupon_applied", {
      ...eventData,
      coupon_code: getValues("couponCode"),
      discount: discountPercentage ? discountPercentage : discount,
      discount_percentage: discountPercentage,
      discount_view: view || "",
    });
  };

  useEffect(() => {
    track("landing_page_visit", eventData);
  }, []);

  useEffect(() => {
    // Function to handle the scroll event
    const throttledTrack = throttle((depth: number) => {
      track("page_scrolled", {
        ...eventData,
        scroll_depth: depth,
      });
    }, 2000);

    const handleScroll = () => {
      throttledTrack(window.scrollY); // Update the scroll depth
    };

    // Attach the scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      track("exit_intent_detected", {
        ...eventData,
        exit_trigger: true,
      });
    };
    // Add the event listener for beforeunload
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup: Remove the event listener when the component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <FormProvider
      control={control}
      watch={watch}
      handleSubmit={handleSubmit}
      getValues={getValues}
      setValue={setValue}
      {...rest}
    >
      <div className="flex flex-col">
        <div className="flex md:hidden mb-6 items-center">
          {data.c_logo_url && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              alt={data.title}
              src={data.c_logo_url}
              className="md:hidden shrink-0 rounded-md h-4 w-auto object-contain mr-4"
            />
          )}
          <h2 className="text-lg font-bold">{data.title}</h2>
        </div>

        <h2 className="text-xl font-semibold mb-4">Payment Details</h2>

        <div className="grid gap-5">
          <Input
            placeholder="Enter Name"
            label="Name"
            control={control}
            name="name"
          />
          <Input
            placeholder="Enter Email"
            label="Email"
            control={control}
            name="email"
          />
          <div className="min-h-[64px] w-full">
            <PhoneInput
              placeholder="Enter Phone"
              label="Phone (WhatsApp enabled to access later)"
              // @ts-ignore
              control={control}
              name="phone"
              defaultCountry={data.__country || "IN"}
              // onCountryChange={onCountryChange}
            />
          </div>

          {data?.kycDetails ? <KycDetails /> : null}

          <CouponCodeField
            planId={data.plan_id}
            webinarId={data._id}
            setDiscount={setDiscount}
            setDiscountPercentage={setDiscountPercentage}
            hideCouponCodeField={hideCouponCodeField}
            trackApplyCouponEvent={trackApplyCouponEvent}
            setDiscountDetails={setDiscountDetails}
          />

          {data.is_gst_needed_explicitly && (
            <Checkbox
              defaultChecked={false}
              control={control}
              name="needGst"
              label="Add GST details (optional)"
            />
          )}

          <GSTFields />

          {data.tnc ? (
            <Checkbox
              defaultChecked={false}
              control={control}
              name="tnc"
              label={
                <>
                  I agree to the{" "}
                  <a
                    className="text-blue-500"
                    target="_blank"
                    rel="noreferrer noopener"
                    href={data.tnc?.[0]?.url || "#"}
                  >
                    terms
                  </a>{" "}
                  and{" "}
                  <a
                    href={data.tnc?.[1]?.url || "#"}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="
                text-blue-500 
              "
                  >
                    conditions
                  </a>
                </>
              }
            />
          ) : null}

          {newTnC ? (
            <Checkbox
              defaultChecked={false}
              control={control}
              name="tnc"
              label={
                <>
                  I agree to the{" "}
                  <a
                    className="text-blue-500"
                    target="_blank"
                    rel="noreferrer noopener"
                    href={newTnC?.href}
                  >
                    terms and conditions
                  </a>
                </>
              }
            />
          ) : null}

          <BundleOffers<Schema> name="offers" />

          <Money
            discount={discount}
            onSubmit={initiatePayment}
            isPaymentLoading={isPaymentLoading}
            discountPercentage={discountPercentage}
            currency={data.currency || "INR"}
            view={view}
            trackPayButtonClickEvent={trackPayButtonClickEvent}
            offeringId={data._id}
            setIsPaymentLoading={setIsPaymentLoading}
          />
        </div>
      </div>
    </FormProvider>
  );
};

const Money = ({
  currency,
  onSubmit,
  discount,
  isPaymentLoading,
  discountPercentage,
  trackPayButtonClickEvent,
  view,
  setIsPaymentLoading,
  offeringId,
}: MoneyCommonProps & {
  onSubmit: (
    totalAmount: number,
    data: Schema,
    method?: string,
    callback?: (event: string) => void
  ) => void;
  trackPayButtonClickEvent: () => void;
  view: View;
}) => {
  const params = useAmount({ discountPercentage, flatDiscount: discount });
  const { handleSubmit } = useFormContext<Schema>();
  const [isFooter, setIsFooter] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [formData, setFormData] = useState<Schema>();
  const [paymentMethodLoading, setPaymentMethodLoading] = useState<
    string | null
  >(null);
  const [modalData, setModalData] =
    useState<SelectPaymentMethodModalData | null>(null);

  const formatter = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
  });
  const formatAmount = (amount: number) => {
    const isINR = currency === "INR";
    const formattedValue = isINR ? Math.ceil(amount) : amount;
    const formatter = new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      minimumFractionDigits: isINR ? 0 : 2,
      maximumFractionDigits: isINR ? 0 : 2,
    });

    return formatter.format(formattedValue);
  };

  const _onSubmit: SubmitHandler<Schema> = useCallback(
    async (data) => {
      trackPayButtonClickEvent();
      setFormData(data);
      setIsPaymentLoading(true);

      try {
        const { data: amountBreakups } = await getAmountBreakups(
          calculateAmount(params?.total ?? 0, currency),
          RIGI_OFFERING_TYPE.COHORT,
          offeringId,
          currency
        );

        setIsPaymentLoading(false);

        const {
          showPaymentMethodModal,
          finalPrices,
          totalRigiFee,
          isOnlyUpiEnabled,
        } = amountBreakups;

        if (showPaymentMethodModal) {
          setShowPaymentMethodModal(true);
          setModalData({
            finalPrices,
            convenienceFee: totalRigiFee,
            isOnlyUpiEnabled,
          });
        } else {
          onSubmit(
            calculateAmount(params?.total ?? 0, currency),
            data,
            isOnlyUpiEnabled ? "upi" : ""
          );
        }
      } catch (error) {
        console.log(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params?.total]
  );

  const handlePaymentMethodSelected = (method: string) => {
    setPaymentMethodLoading(method);

    if (formData) {
      onSubmit(
        calculateAmount(params?.total ?? 0, currency),
        formData,
        method,
        () => {
          setShowPaymentMethodModal(false);
          setPaymentMethodLoading(null);
        }
      );
    }
  };

  const finalPrice = params?.total;

  const handleFooterPayment = () => {
    if (isPaymentLoading) return;
    setIsFooter(true);
    handleSubmit(_onSubmit)();
  };
  const handlePayment = () => {
    setIsFooter(false);
    handleSubmit(_onSubmit)();
  };

  return (
    <div className="flex flex-col">
      <table className="table table-auto mb-6">
        <tbody>
          {params?.originalAmount ? (
            <tr>
              <td className="py-1">Original Price</td>
              <td className="py-1 text-right">
                {formatter.format(params.originalAmount)}
              </td>
            </tr>
          ) : null}
          {params?.bumpUp ? (
            <tr>
              <td className="py-1">Bump up Price</td>
              <td className="py-1 text-right">
                {formatter.format(params.bumpUp)}
              </td>
            </tr>
          ) : null}

          {params?.discount ? (
            <tr>
              <td className="py-1">
                {params?.bumpUp ? "Discount (Original Price)" : "Discount"}
              </td>
              <td className="py-1 text-right">
                -{formatter.format(params?.discount)}
              </td>
            </tr>
          ) : null}
          {params?.gst ? (
            <tr>
              <td className="py-1 pb-4">GST</td>
              <td className="py-1 pb-4 text-right">
                {formatter.format(params.gst)}
              </td>
            </tr>
          ) : null}
          {finalPrice ? (
            <tr>
              <td className="border-t border-grey-400 font-bold py-2">Total</td>
              <td className="border-t border-grey-400 font-bold text-right">
                {formatter.format(finalPrice)}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <SelectPaymentMethod
        isOnlyUpiEnabled={modalData?.isOnlyUpiEnabled}
        convenienceFee={modalData?.convenienceFee}
        show={showPaymentMethodModal}
        setShow={setShowPaymentMethodModal}
        onPaymentMethodSelected={handlePaymentMethodSelected}
        loadingMethod={paymentMethodLoading}
        setLoadingMethod={setPaymentMethodLoading}
        prices={modalData?.finalPrices}
        currency={currency}
      />

      {view !== "PHONE_LANDING" && (
        <div className="block md:hidden">
          <MobilePaymentFooter
            makePayment={() => handleFooterPayment()}
            finalPrice={finalPrice ? formatAmount(finalPrice) : null}
            isPaymentLoading={
              isFooter && !showPaymentMethodModal && isPaymentLoading
            }
            listPrice={
              params
                ? formatAmount(params.originalAmount + (params?.gst || 0))
                : undefined
            }
          />
        </div>
      )}
      <button
        disabled={
          !finalPrice ||
          (!isFooter && !showPaymentMethodModal && isPaymentLoading)
        }
        className="mt-auto w-full h-12 bg-black-600 flex justify-center items-center rounded-md border-none outline-none font-bold text-white hover:bg-primary"
        onClick={handlePayment}
      >
        {!showPaymentMethodModal && !isFooter && isPaymentLoading ? (
          <Loading />
        ) : (
          `Pay ${finalPrice ? formatAmount(finalPrice) : null}`
        )}
      </button>
    </div>
  );
};

const calculate = ({
  amount,
  flatDiscount,
  discountPercentage,
  hasGst,
  gstRate,
}: {
  amount: number;
  flatDiscount?: number | null;
  discountPercentage?: number | null;
  hasGst: boolean;
  gstRate?: number;
}) => {
  const TAX_RATE = typeof gstRate !== "undefined" ? gstRate : 0.18;

  const discount =
    flatDiscount ??
    (discountPercentage ? (amount * discountPercentage) / 100 : 0);

  const net = amount - discount;

  const gst = hasGst ? net * TAX_RATE : 0;

  const total = net + gst;

  return {
    amount,
    discount,
    net,
    gst,
    total,
  };
};

const useAmount = ({
  flatDiscount,
  discountPercentage,
}: {
  flatDiscount?: number | null;
  discountPercentage?: number | null;
}) => {
  const { data } = useContext(dataContext);

  const { watch } = useFormContext<Schema>();

  const [offers] = watch(["offers"]);

  const appliedOffers = useMemo(() => {
    if (!offers) return [];

    return offers.filter((offer: BundleOffer) => offer.isApplied);
  }, [offers]);

  const params = useMemo(() => {
    if (!data) return;

    const {
      amount: baseAmount,
      discount: baseDiscount,
      gst: baseGst,
      total: baseTotal,
    } = calculate({
      flatDiscount,
      amount: data.price,
      discountPercentage,
      hasGst: data.is_gst_needed_explicitly ?? false,
    });

    const totalOfferAmount = appliedOffers?.reduce(
      ({ amount, discount, gst, total }, offer: BundleOffer) => {
        const {
          amount: _amount,
          discount: _discount,
          total: _total,
          gst: _gst,
        } = calculate({
          amount: offer.price,
          hasGst: !!offer.gstValue,
          gstRate: offer.gstValue,
        });

        return {
          amount: amount + _amount,
          discount: discount + _discount,
          gst: gst + _gst,
          total: total + _total,
        };
      },
      {
        amount: 0,
        discount: 0,
        gst: 0,
        total: 0,
      }
    ) as {
      amount: number;
      discount: number;
      gst: number;
      total: number;
    };

    const total = baseTotal + totalOfferAmount.total;
    const gst = baseGst + totalOfferAmount.gst;
    const originalAmount = baseAmount;
    const discount = baseDiscount + totalOfferAmount.discount;
    const bumpUp = totalOfferAmount.amount;

    return {
      originalAmount,
      discount,
      gst,
      total,
      bumpUp,
    };
  }, [flatDiscount, appliedOffers, data, discountPercentage]);

  return params;
};

const useVerifyOffer = () => {
  const [data, setData] =
    useState<Awaited<ReturnType<typeof verifyOfferMutation>>>();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const reset = useCallback(() => {
    setData(undefined);
    setError(undefined);
  }, []);

  const mutate = useCallback(
    async (webinarId: string, code: string, planId?: string) => {
      try {
        setLoading(true);
        const data = await verifyOfferMutation({
          planId,
          code: code,
          channelId: webinarId,
        });

        setData(data);
        setError(undefined);
      } catch (error: any) {
        setData(undefined);
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { data, error, isLoading, mutate, reset };
};

const CouponCodeField = memo(
  ({
    planId,
    webinarId,
    setDiscount,
    setDiscountPercentage,
    hideCouponCodeField,
    trackApplyCouponEvent,
    setDiscountDetails,
  }: {
    planId?: string;
    webinarId?: string;
    setDiscount: (discount: number | null) => void;
    setDiscountPercentage: (percentage: number | null) => void;
    hideCouponCodeField?: boolean;
    trackApplyCouponEvent: (view: string) => void;
    setDiscountDetails: React.Dispatch<React.SetStateAction<DiscountDetails>>;
  }) => {
    const {
      control,
      getValues,
      formState: { touchedFields, dirtyFields },
      resetField,
    } = useFormContext<Schema>();

    const { data, mutate, error, isLoading, reset } = useVerifyOffer();

    const applyCoupon = useCallback(async () => {
      const couponCode = getValues("couponCode");
      if (!webinarId || !couponCode || couponCode.trim().length === 0) return;
      trackApplyCouponEvent(data?.data?.view || "");
      resetField("couponCode", {
        keepDirty: false,
        keepTouched: false,
        defaultValue: couponCode,
      });

      await mutate(webinarId, couponCode, planId);
    }, [getValues, planId, webinarId, resetField, mutate]);

    const removeCoupon = useCallback(() => {
      resetField("couponCode", {
        keepDirty: false,
        keepTouched: false,
        defaultValue: "",
      });

      reset();
    }, [reset, resetField]);

    const discountPercentage = data?.data.discount_percentage;
    const discount = data?.data.discounted_price;
    const discountView = data?.data?.view;

    useEffect(() => {
      setDiscount(discount ?? null);
    }, [discount, setDiscount]);

    useEffect(() => {
      setDiscountPercentage(discountPercentage ?? null);
    }, [discountPercentage, setDiscountPercentage]);

    useEffect(() => {
      setDiscountDetails((prev) => ({
        ...prev,
        discount: discountPercentage ? discountPercentage : discount || 0,
        discountType: discountPercentage ? "percentage" : "flat",
        discountVisibility: discountView || "",
      }));
    }, [discount, discountPercentage, discountView, setDiscountDetails]);

    const hasDiscount =
      typeof discountPercentage !== "undefined" ||
      typeof discount !== "undefined";

    const searchParams = useSearchParams();

    const initialCouponCode = searchParams.get("code");

    useEffect(() => {
      if (initialCouponCode) {
        resetField("couponCode", {
          keepDirty: false,
          keepTouched: false,
          defaultValue: initialCouponCode,
        });

        applyCoupon();
      }
    }, [applyCoupon, initialCouponCode, resetField]);

    return (
      <div
        className="relative"
        style={{ display: hideCouponCodeField ? "none" : "block" }}
      >
        <>
          <Input
            placeholder="Enter Coupon Code"
            label="Coupon Code"
            control={control}
            name="couponCode"
            className="pr-16"
            readOnly={hasDiscount}
            error={(error as any)?.response?.data?.message}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyCoupon();
              }
            }}
          />
          {!hasDiscount && (
            <button
              disabled={isLoading}
              onClick={applyCoupon}
              className="absolute font-medium text-sm text-[#202020] z-10 right-2 top-[34px] disabled:opacity-50 transition-opacity"
            >
              Apply
            </button>
          )}
          {hasDiscount && (
            <button
              disabled={isLoading}
              onClick={removeCoupon}
              className="absolute font-medium text-sm text-[#202020] z-10 right-2 top-[34px] disabled:opacity-50 transition-opacity"
            >
              <XMarkIcon className="w-5 h-5 text-[#202020]" />
            </button>
          )}
        </>
        {hasDiscount && !dirtyFields["couponCode"] && (
          <small className="text-xs text-green-600">
            Offer applied successfully
          </small>
        )}
      </div>
    );
  }
);

CouponCodeField.displayName = "CouponCodeField";

const GSTFields = memo(() => {
  const { watch, control } = useFormContext<Schema>();
  const needGst = watch("needGst");

  if (!needGst) return null;

  return (
    <>
      <Input
        placeholder="Enter GSTIN"
        label="GSTIN"
        control={control}
        name="gstin"
      />
      <Input
        placeholder="Enter Company Name"
        label="Company Name"
        control={control}
        name="companyName"
      />
    </>
  );
});

GSTFields.displayName = "GSTFields";

const Loading = () => {
  return (
    <svg
      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
};

const KycDetails = () => {
  const { control } = useFormContext<Schema>();
  const { fields } = useFieldArray({
    control,
    name: "kycDetails",
  });

  return fields.map((field, index) => {
    switch (field.type) {
      case "text_field":
        return (
          <Input
            key={field.id}
            placeholder={field.placeholder || `Enter ${field.label}`}
            label={field.label}
            control={control}
            name={`kycDetails.${index}.answer`}
            required={field.required}
            regex={field.regex ? new RegExp(field.regex) : undefined}
          />
        );
      case "dropdown":
        return (
          <Select
            key={field.id}
            placeholder={field.placeholder || `Select ${field.label}`}
            label={field.label}
            control={control}
            name={`kycDetails.${index}.answer`}
            defaultValue={field.option?.[0] || ""}
            required={field.required}
            options={
              field.option?.map((option) => ({
                label: option,
                value: option,
              })) || []
            }
          />
        );
      case "date_picker":
        return (
          <DatePicker
            key={field.id}
            placeholder={field.placeholder || `Select ${field.label}`}
            label={field.label}
            control={control}
            required={field.required}
            canNotBeFuture={field.key === "dob"}
            name={`kycDetails.${index}.answer`}
          />
        );
    }
  });
};

export default Payment;
